import { useEffect, useState, useMemo, useCallback } from "react"


import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { Link } from "react-router-dom"

import { handleApiError, usersService, ordersService, packageMappingsService, type AdNameLookupItem, type User, type Order, type OrderStatus, type PackageMappingsResponse, UserType } from "../api"
import StatusInfoButton from "../components/orders/status-info-button"
import PatientProfileExportCard from "../components/tools/patient-profile-export-card"
import { useUser } from "../hooks/use-user"
import { hasAnyRole } from "../lib/user-utils"

const ORDER_STATUS_OPTIONS: { value: OrderStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'purchased', label: 'Purchased' },
  { value: 'created', label: 'Created' },
  { value: 'received', label: 'Received' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'deleted', label: 'Deleted' },
]

type AccountAge = 0 | 1 | 3 | 6 | 12

type VerificationFilter = 'all' | 'verified' | 'unverified'

type UserTypeFilter = 'all' | 'regular' | 'content-only'

type OrderFilter = 'all' | 'with-orders' | 'no-orders'

const ACCOUNT_AGE_OPTIONS: { value: AccountAge; label: string }[] = [
  { value: 0, label: 'No minimum' },
  { value: 1, label: '1 month' },
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
]

const MAX_ACCOUNT_AGE_OPTIONS: { value: AccountAge; label: string }[] = [
  { value: 0, label: 'No maximum' },
  { value: 1, label: '1 month' },
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
]

const isOlderThanMonths = (createdAt: string, months: number): boolean => {
  if (months === 0) return true
  const created = new Date(createdAt)
  const cutoff = new Date()
  cutoff.setMonth(cutoff.getMonth() - months)
  return created <= cutoff
}

const isYoungerThanMonths = (createdAt: string, months: number): boolean => {
  if (months === 0) return true
  const created = new Date(createdAt)
  const cutoff = new Date()
  cutoff.setMonth(cutoff.getMonth() - months)
  return created >= cutoff
}

const matchesVerification = (user: User, filter: VerificationFilter): boolean => {
  if (filter === 'all') return true
  if (filter === 'verified') return user.isVerified
  return !user.isVerified
}

const matchesUserType = (user: User, filter: UserTypeFilter): boolean => {
  if (filter === 'all') return true
  if (filter === 'regular') return user.userType === UserType.REGULAR
  return user.userType === UserType.CONTENT_ONLY
}

const parseEmailList = (input: string): string[] => {
  const seen = new Set<string>()
  const emails: string[] = []
  for (const token of input.split(/[\s,;]+/)) {
    const email = token.trim().toLowerCase()
    if (!email || seen.has(email)) continue
    seen.add(email)
    emails.push(email)
  }
  return emails
}

const formatDate = (value: string | null): string => {
  if (!value) return ''
  const date = new Date(value)
  return isNaN(date.getTime()) ? '' : (date.toISOString().split('T')[0] ?? '')
}

const csvEscape = (value: unknown): string => {
  const str = value == null ? '' : String(value)
  const needsQuoting = /[",\n]/.test(str)
  const escaped = str.replace(/"/g, '""')
  return needsQuoting ? `"${escaped}"` : escaped
}

const formatDateTime = (value: string): string => {
  const date = new Date(value)
  if (isNaN(date.getTime())) return ''
  const yyyyMmDd = date.toISOString().split('T')[0]
  const hhMm = date.toTimeString().split(' ')[0]?.slice(0, 5) || ''
  return `${yyyyMmDd} ${hhMm}`
}

const userTypeLabel = (userType: UserType): string =>
  userType === UserType.CONTENT_ONLY ? 'Content-Only' : 'Regular'

const sortByCreatedAtDesc = (users: User[]): User[] =>
  [...users].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))

export default function ToolsPage() {
  const { user } = useUser()
  const canSeeMarketingTools = hasAnyRole(user, ['admin', 'marketing'])
  const canSeePatientExport = hasAnyRole(user, ['admin', 'doctor'])
  const canSeeOrderExport = hasAnyRole(user, ['admin', 'marketing', 'support'])

  const [allUsers, setAllUsers] = useState<User[]>([])
  const [userIdsWithOrders, setUserIdsWithOrders] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [copySuccess, setCopySuccess] = useState(false)

  const [packageMappings, setPackageMappings] = useState<PackageMappingsResponse | null>(null)
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([])
  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatus | 'all'>('all')
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000)
    return d.toISOString().slice(0, 16)
  })
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 16))
  const [orderCopySuccess, setOrderCopySuccess] = useState(false)

  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [isOrdersLoading, setIsOrdersLoading] = useState(false)

  const [lookupInput, setLookupInput] = useState("")
  const [lookupResults, setLookupResults] = useState<{ id: string; user?: User; error?: string }[]>([])
  const [isLookingUp, setIsLookingUp] = useState(false)

  const [adLookupInput, setAdLookupInput] = useState("")
  const [adLookupResults, setAdLookupResults] = useState<AdNameLookupItem[]>([])
  const [adLookupError, setAdLookupError] = useState("")
  const [isAdLookingUp, setIsAdLookingUp] = useState(false)

  const [minAge, setMinAge] = useState<AccountAge>(0)
  const [maxAge, setMaxAge] = useState<AccountAge>(0)
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('all')
  const [userTypeFilter, setUserTypeFilter] = useState<UserTypeFilter>('all')
  const [orderFilter, setOrderFilter] = useState<OrderFilter>('all')

  useEffect(() => {
    if (!canSeeMarketingTools && !canSeeOrderExport) {
      setIsLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError("")
        if (canSeeMarketingTools) {
          const [users, userIds] = await Promise.all([
            usersService.getExportUsers("user"),
            ordersService.getUserIdsWithOrders(),
          ])
          setAllUsers(sortByCreatedAtDesc(users))
          setUserIdsWithOrders(new Set(userIds))
        }
        try {
          const mappings = await packageMappingsService.getPackageMappings()
          setPackageMappings(mappings)
        } catch {
          // Package mappings are non-critical — cards still work with raw IDs
        }
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [canSeeMarketingTools, canSeeOrderExport])

  const [ordersFetched, setOrdersFetched] = useState(false)

  const fetchFilteredOrders = useCallback(async () => {
    try {
      setIsOrdersLoading(true)
      const response = await ordersService.getOrders({
        status: orderStatusFilter !== 'all' ? orderStatusFilter : undefined,
        packageIds: selectedPackageIds.length > 0 ? selectedPackageIds.join(',') : undefined,
        dateFrom: dateFrom ? dateFrom + 'Z' : undefined,
        dateTo: dateTo ? dateTo + ':59.999Z' : undefined,
        limit: 10000,
      })
      setFilteredOrders(response.items)
      setOrdersFetched(true)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsOrdersLoading(false)
    }
  }, [orderStatusFilter, selectedPackageIds, dateFrom, dateTo])

  const filteredUsers = useMemo(() =>
    allUsers.filter(u => {
      if (!isOlderThanMonths(u.createdAt, minAge)) return false
      if (!isYoungerThanMonths(u.createdAt, maxAge)) return false
      if (!matchesVerification(u, verificationFilter)) return false
      if (!matchesUserType(u, userTypeFilter)) return false

      if (orderFilter === 'with-orders' && !userIdsWithOrders.has(u.id)) return false
      if (orderFilter === 'no-orders' && userIdsWithOrders.has(u.id)) return false

      return true
    }),
    [allUsers, minAge, maxAge, verificationFilter, userTypeFilter, orderFilter, userIdsWithOrders]
  )

  const resolvePackage = useCallback((packageId: string) => {
    if (!packageMappings) return { name: packageId, isContentOnly: false, env: 'unknown' as const }
    const prodPkg = packageMappings.prod[packageId]
    const devPkg = packageMappings.dev[packageId]
    const pkg = prodPkg ?? devPkg
    return {
      name: pkg ? pkg.name : packageId,
      isContentOnly: pkg?.isContentOnly === true,
      env: (prodPkg ? 'prod' : devPkg ? 'sandbox' : 'unknown') as 'prod' | 'sandbox' | 'unknown',
    }
  }, [packageMappings])

  const packageOptions = useMemo(() => {
    if (!packageMappings) return []
    const allPackageIds = [
      ...Object.keys(packageMappings.prod),
      ...Object.keys(packageMappings.dev),
    ]
    const uniqueIds = [...new Set(allPackageIds)]
    return uniqueIds
      .map(id => {
        const resolved = resolvePackage(id)
        return { value: id, label: `${resolved.name} (${id})`, isContentOnly: resolved.isContentOnly, env: resolved.env }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [packageMappings, resolvePackage])

  const prodPackageOptions = useMemo(() => packageOptions.filter(o => o.env === 'prod'), [packageOptions])
  const sandboxPackageOptions = useMemo(() => packageOptions.filter(o => o.env !== 'prod'), [packageOptions])

  const selectPackagesByType = (contentOnly: boolean) => {
    const ids = packageOptions.filter(o => o.isContentOnly === contentOnly).map(o => o.value)
    if (ids.length === 0) return
    setSelectedPackageIds(ids)
  }

  const uniqueOrderUsers = useMemo(() => {
    const seen = new Set<string>()
    const users: User[] = []
    for (const order of filteredOrders) {
      if (seen.has(order.userId)) continue
      seen.add(order.userId)
      users.push(order.user)
    }
    return users
  }, [filteredOrders])

  const handleCopyOrderEmails = async () => {
    try {
      const emails = uniqueOrderUsers.map(u => u.email).join(', ')
      await navigator.clipboard.writeText(emails)
      setOrderCopySuccess(true)
      setTimeout(() => setOrderCopySuccess(false), 2000)
    } catch {
      setError('Failed to copy to clipboard')
    }
  }

  const handleExportOrderCsv = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'Package ID', 'Package Name', 'Order Status', 'Order Date']

    const rows = filteredOrders.map(o => {
      return [
        csvEscape(o.user.email),
        csvEscape(o.user.firstName || ''),
        csvEscape(o.user.lastName || ''),
        csvEscape(o.packageId ?? ''),
        csvEscape(o.packageId ? resolvePackage(o.packageId).name : ''),
        csvEscape(o.status),
        csvEscape(formatDateTime(o.createdAt)),
      ].join(',')
    })

    const bom = '\ufeff'
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const dateStamp = new Date().toISOString().split('T')[0]
    a.href = url
    a.download = `order-email-export-${dateStamp}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleCopyEmails = async () => {
    try {
      const emails = filteredUsers.map(u => u.email).join(', ')
      await navigator.clipboard.writeText(emails)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch {
      setError('Failed to copy to clipboard')
    }
  }

  const handleExportCsv = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'User Type', 'Verified', 'Created At']

    const rows = filteredUsers.map(u => [
      csvEscape(u.email),
      csvEscape(u.firstName || ''),
      csvEscape(u.lastName || ''),
      csvEscape(userTypeLabel(u.userType)),
      csvEscape(u.isVerified ? 'Yes' : 'No'),
      csvEscape(formatDateTime(u.createdAt)),
    ].join(','))

    const bom = '\ufeff'
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const dateStamp = new Date().toISOString().split('T')[0]
    a.href = url
    a.download = `email-export-${dateStamp}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleUserLookup = async () => {
    const ids = lookupInput
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
    if (ids.length === 0) return

    setIsLookingUp(true)
    setLookupResults([])

    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          const user = await usersService.getUserById(id)
          return { id, user }
        } catch {
          return { id, error: 'Not found' }
        }
      })
    )
    setLookupResults(results)
    setIsLookingUp(false)
  }

  const adLookupEmails = useMemo(() => parseEmailList(adLookupInput), [adLookupInput])
  const adLookupMatched = useMemo(
    () => adLookupResults.filter(r => r.adName).length,
    [adLookupResults],
  )
  const adLookupFound = useMemo(
    () => adLookupResults.filter(r => r.found).length,
    [adLookupResults],
  )

  const handleAdNameLookup = async () => {
    if (adLookupEmails.length === 0) return
    setIsAdLookingUp(true)
    setAdLookupError("")
    setAdLookupResults([])
    try {
      const response = await usersService.lookupAdNames(adLookupEmails)
      setAdLookupResults(response.items)
    } catch (err) {
      setAdLookupError(handleApiError(err))
    } finally {
      setIsAdLookingUp(false)
    }
  }

  const handleAdCsvUpload = async (file: File) => {
    const text = await file.text()
    // Pull emails out of any column — ignores headers and extra fields.
    const emails = [...text.matchAll(/[^\s,;"']+@[^\s,;"']+\.[^\s,;"']+/g)].map(m => m[0])
    if (emails.length === 0) {
      setAdLookupError('No email addresses found in the CSV')
      return
    }
    setAdLookupError("")
    setAdLookupInput(emails.join('\n'))
  }

  const handleExportAdNamesCsv = () => {
    const headers = ['Email', 'Found', 'Ad Name', 'Adset ID', 'Purchase Date']
    const rows = adLookupResults.map(r => [
      csvEscape(r.email),
      csvEscape(r.found ? 'Yes' : 'No'),
      csvEscape(r.adName ?? ''),
      csvEscape(r.adsetId ?? ''),
      csvEscape(formatDate(r.purchasedAt)),
    ].join(','))

    const bom = '\ufeff'
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const dateStamp = new Date().toISOString().split('T')[0]
    a.href = url
    a.download = `ad-name-export-${dateStamp}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (isLoading && (canSeeMarketingTools || canSeeOrderExport)) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Card sx={{ height: 'fit-content' }}>
          <CardContent sx={{ p: 3 }}>
            <Skeleton variant="text" width={300} height={40} />
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 3 }}>
        Tools
      </Typography>

      {canSeePatientExport && (
        <Box sx={{ mb: 3 }}>
          <PatientProfileExportCard />
        </Box>
      )}

      {canSeeMarketingTools && (
      <>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Email List Export
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Min Account Age</InputLabel>
              <Select
                value={minAge}
                label="Min Account Age"
                onChange={(e) => setMinAge(e.target.value as AccountAge)}
              >
                {ACCOUNT_AGE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Max Account Age</InputLabel>
              <Select
                value={maxAge}
                label="Max Account Age"
                onChange={(e) => setMaxAge(e.target.value as AccountAge)}
              >
                {MAX_ACCOUNT_AGE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Verification</InputLabel>
              <Select
                value={verificationFilter}
                label="Verification"
                onChange={(e) => setVerificationFilter(e.target.value as VerificationFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="verified">Verified only</MenuItem>
                <MenuItem value="unverified">Unverified only</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userTypeFilter}
                label="User Type"
                onChange={(e) => setUserTypeFilter(e.target.value as UserTypeFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="content-only">Content-Only</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Orders</InputLabel>
              <Select
                value={orderFilter}
                label="Orders"
                onChange={(e) => setOrderFilter(e.target.value as OrderFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="with-orders">With orders</MenuItem>
                <MenuItem value="no-orders">No orders</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" color="text.secondary">
              {filteredUsers.length} users match
            </Typography>

            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyEmails}
              disabled={filteredUsers.length === 0}
              color={copySuccess ? 'success' : 'primary'}
            >
              {copySuccess ? 'Copied!' : 'Copy Emails'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={filteredUsers.length === 0}
            >
              Export CSV
            </Button>
          </Box>
        </CardContent>
      </Card>

      </>
      )}

      {canSeeOrderExport && (
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Email Export by Order
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 280, maxWidth: 480 }}>
              <InputLabel shrink>Package</InputLabel>
              <Select
                multiple
                displayEmpty
                value={selectedPackageIds}
                label="Package"
                onChange={(e) => setSelectedPackageIds(e.target.value as string[])}
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All'
                    : selected.map(id => resolvePackage(id).name).join(', ')
                }
              >
                {prodPackageOptions.length > 0 && <ListSubheader>Production</ListSubheader>}
                {prodPackageOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={selectedPackageIds.includes(opt.value)} size="small" />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
                {sandboxPackageOptions.length > 0 && <ListSubheader>Sandbox</ListSubheader>}
                {sandboxPackageOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={selectedPackageIds.includes(opt.value)} size="small" />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip label="Blood" size="small" variant={selectedPackageIds.length > 0 && selectedPackageIds.every(id => !resolvePackage(id).isContentOnly) ? 'filled' : 'outlined'} onClick={() => selectPackagesByType(false)} />
              <Chip label="Content" size="small" variant={selectedPackageIds.length > 0 && selectedPackageIds.every(id => resolvePackage(id).isContentOnly) ? 'filled' : 'outlined'} onClick={() => selectPackagesByType(true)} />
              {selectedPackageIds.length > 0 && (
                <Chip label="Clear" size="small" variant="outlined" onDelete={() => setSelectedPackageIds([])} />
              )}
            </Box>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Order Status</InputLabel>
              <Select
                value={orderStatusFilter}
                label="Order Status"
                onChange={(e) => setOrderStatusFilter(e.target.value as OrderStatus | 'all')}
              >
                {ORDER_STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="From (UTC)"
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 200 }}
            />

            <TextField
              size="small"
              label="To (UTC)"
              type="datetime-local"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 200 }}
            />
          </Box>

          <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0 }}>
            <Typography variant="body2">Blood/Content presets are a shortcut — double-check the selected packages.</Typography>
            <Typography variant="body2">Emails are deduplicated — users with multiple orders appear once in Copy Emails.</Typography>
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={isOrdersLoading ? <CircularProgress size={16} /> : <SearchIcon />}
              onClick={fetchFilteredOrders}
              disabled={isOrdersLoading}
            >
              {isOrdersLoading ? 'Searching...' : 'Search Orders'}
            </Button>

            {ordersFetched && !isOrdersLoading && (
              <Typography variant="body1" color="text.secondary">
                {filteredOrders.length} orders from {uniqueOrderUsers.length} unique users
              </Typography>
            )}

            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyOrderEmails}
              disabled={uniqueOrderUsers.length === 0 || isOrdersLoading}
              color={orderCopySuccess ? 'success' : 'primary'}
            >
              {orderCopySuccess ? 'Copied!' : 'Copy Emails'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportOrderCsv}
              disabled={filteredOrders.length === 0 || isOrdersLoading}
            >
              Export CSV
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}

      {canSeeMarketingTools && (
      <>
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            Ad Name Lookup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste a list of emails — or upload a CSV of them — to get the ad (utm_ad_name) each user purchased from. Only web-funnel purchases carry an ad name; app-store purchases and users who never bought show up blank.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              multiline
              minRows={4}
              maxRows={12}
              size="small"
              label="Emails (one per line, or comma-separated)"
              value={adLookupInput}
              onChange={(e) => setAdLookupInput(e.target.value)}
              sx={{ flex: 1, maxWidth: 480 }}
              placeholder={"jane@example.com\nbob@example.com"}
              helperText={adLookupEmails.length > 0 ? `${adLookupEmails.length} unique email${adLookupEmails.length === 1 ? '' : 's'}` : ' '}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={isAdLookingUp ? <CircularProgress size={16} /> : <SearchIcon />}
              onClick={handleAdNameLookup}
              disabled={isAdLookingUp || adLookupEmails.length === 0}
            >
              {isAdLookingUp ? 'Looking up...' : 'Look up'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={isAdLookingUp}
            >
              Upload CSV
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleAdCsvUpload(file)
                  e.target.value = ''
                }}
              />
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportAdNamesCsv}
              disabled={adLookupResults.length === 0}
            >
              Export CSV
            </Button>
            {adLookupResults.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {adLookupFound} of {adLookupResults.length} found · {adLookupMatched} with ad name
              </Typography>
            )}
          </Box>

          {adLookupError && (
            <Alert severity="error" sx={{ mb: 2 }}>{adLookupError}</Alert>
          )}

          {adLookupResults.length > 0 && (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Ad Name</TableCell>
                    <TableCell>Adset ID</TableCell>
                    <TableCell>Purchase Date</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adLookupResults.map((r) => (
                    <TableRow key={r.email}>
                      <TableCell>{r.email}</TableCell>
                      {r.found ? (
                        <>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                            {r.adName ?? <Typography variant="body2" color="text.secondary" component="span">No ad-attributed purchase</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.adsetId ?? ''}</TableCell>
                          <TableCell>{formatDate(r.purchasedAt)}</TableCell>
                          <TableCell>
                            {r.userId && (
                              <Link to={`/users/${r.userId}`} style={{ textDecoration: 'none', color: '#B87658' }}>
                                Open
                              </Link>
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="error">User not found</Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            User Lookup
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              multiline
              minRows={3}
              maxRows={10}
              size="small"
              label="User IDs (one per line)"
              value={lookupInput}
              onChange={(e) => setLookupInput(e.target.value)}
              sx={{ flex: 1, maxWidth: 480 }}
              placeholder={"054ed5f4-7cec-48a4-b204-...\na1b2c3d4-..."}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={isLookingUp ? <CircularProgress size={16} /> : <SearchIcon />}
            onClick={handleUserLookup}
            disabled={isLookingUp || lookupInput.trim().length === 0}
            sx={{ mb: 2 }}
          >
            {isLookingUp ? 'Looking up...' : 'Look up'}
          </Button>

          {lookupResults.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Orders</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lookupResults.map((r) => {
                    const userOrders = r.user ? filteredOrders.filter(o => o.userId === r.id) : []
                    return (
                    <TableRow key={r.id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.id}</TableCell>
                      {r.user ? (
                        <>
                          <TableCell>{r.user.email}</TableCell>
                          <TableCell>{formatDateTime(r.user.createdAt)}</TableCell>
                          <TableCell>{formatDateTime(r.user.updatedAt)}</TableCell>
                          <TableCell>
                            {!ordersFetched ? (
                              <Typography variant="body2" color="text.secondary">Search orders first</Typography>
                            ) : userOrders.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">None</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {userOrders.map(o => (
                                  <Chip
                                    key={o.id}
                                    label={`${o.status}${o.packageId ? ` · ${resolvePackage(o.packageId).name}` : ''}`}
                                    size="small"
                                    variant="outlined"
                                    color={o.status === 'completed' ? 'success' : o.status === 'failed' || o.status === 'cancelled' ? 'error' : 'default'}
                                    component={Link}
                                    to={`/orders/${o.id}`}
                                    clickable
                                    sx={{ fontSize: '0.75rem' }}
                                  />
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Link to={`/users/${r.id}`} style={{ textDecoration: 'none', color: '#B87658' }}>
                              Open
                            </Link>
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="error">Not found — try checking the dev admin</Typography>
                        </TableCell>
                      )}
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <StatusInfoButton />
      </>
      )}
    </Box>
  )
}
